// Atlas · Deployment — three months, one card per day.
//
// THE QUESTION THIS PAGE ANSWERS: on any given day, how many screens did we
// have in cabs, and how many of them actually ran? Those two numbers are easy
// to conflate and the distance between them is the whole state of the fleet.
//
// Everything is served by /api/atlas/calendar, including the month grids and
// the leading blanks — date arithmetic belongs next to the timezone, not in a
// component. This file renders and nothing else.
//
// THREE DAY STATES, and each is a different fact:
//   full     every deployed screen ran
//   partial  some ran; the shortfall is the number to look at
//   dark     screens were deployed and none of them ran
// A fourth marker, `lag`, is for days when MORE screens ran than the registry
// says were deployed — the ten Pune bindings were all written on 10 Aug but
// those cabs were delivering from the 5th. Those days are marked, not clamped.

import React, { useMemo, useState } from 'react';
import Term from '../Term';
import useApi from '../../v2/lib/useApi';
import api from '../../services/api';

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// THE MEASURE SWITCH. Six figures on a 90px day card is not information, it is
// wallpaper — so the day cards carry the screens that ran plus ONE measure at a
// time, and the month card, which has a whole column of height, carries all of
// them at once. Nothing is hidden; it is a question of which grain has the room.
const MEASURES = [
  { id: 'rupees', label: 'Revenue',  short: '₹',     fmt: (v) => (v > 0 ? inr(v) : '—') },
  { id: 'km',     label: 'Km',       short: 'km',    fmt: (v) => (v > 0 ? `${n(v)} km` : '—') },
  { id: 'trips',  label: 'Trips',    short: 'trips', fmt: (v) => (v > 0 ? `${n(v)}` : '—') },
  { id: 'paxHours', label: 'Passenger hrs', short: 'pax h', fmt: (v) => (v > 0 ? `${n(v)}h` : '—') },
  { id: 'driveHours', label: 'Driving hrs', short: 'drive h', fmt: (v) => (v > 0 ? `${n(v)}h` : '—') },
];
const measureOf = (id) => MEASURES.find((m) => m.id === id) || MEASURES[0];

function dayState(d) {
  if (d.deployed.total === 0) return 'none';
  // Before the network's first delivered ad a fitted screen is waiting, not
  // failing. Marking these dark would put seventeen red days in July and make
  // the September shortfall — the real signal — look like more of the same.
  if (d.preService) return 'pre';
  if (d.active.total === 0) return 'dark';
  if (d.active.total >= d.deployed.total) return 'full';
  return 'partial';
}

function DayCard({ d, milestone, isToday, selected, onSelect, measure }) {
  const state = dayState(d);
  const cls = [
    'day', `is-${state}`,
    d.registryLag ? 'is-lag' : '',
    isToday ? 'is-today' : '',
    selected ? 'is-selected' : '',
    milestone ? 'is-milestone' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={cls}
      onClick={() => onSelect(d)}
      aria-label={`${d.day}, ${d.active.total} of ${d.deployed.total} screens active`}
    >
      <span className="dayHead">
        <span className="dayNum">{Number(d.day.slice(8, 10))}</span>
        <span className="dayDow">{d.dow}</span>
      </span>

      {d.deployed.total === 0 ? (
        <span className="dayEmpty">—</span>
      ) : state === 'pre' ? (
        // Nothing to compare against yet, so no ratio and no bar — just what
        // was in the ground. A "0/2" here would read as a failure.
        <>
          <span className="dayFig">
            <span className="figFitted">{d.deployed.total}</span>
          </span>
          <span className="dayWaiting">fitted · not serving</span>
        </>
      ) : (
        <>
          <span className="dayFig">
            <span className="figActive">{d.active.total}</span>
            <span className="figSlash">/</span>
            <span className="figDeployed">{d.deployed.total}</span>
            {d.registryLag
              ? <span className="dayPct isLag" title={`Delivered before the registry recorded the binding — ${inr(d.lag?.rupees || 0)} outside this page's totals`}>ahead</span>
              : d.ranPct != null && <span className="dayPct">{d.ranPct}%</span>}
          </span>
          <span className="dayMoney">{measure.fmt(d[measure.id])}</span>
          <span className="dayCities">
            <span className="cityMum" title="Mumbai">M {d.active.mumbai}<i>/{d.deployed.mumbai}</i></span>
            <span className="cityPun" title="Pune">P {d.active.pune}<i>/{d.deployed.pune}</i></span>
          </span>
          {/* The day gate — the number that decides what the fleet partner is
              owed for that day. Frozen verdicts, scored by the rule that was
              in force; "of N" is the count actually scored, not the fleet. */}
          <span
            className={`dayGate${d.gate ? (d.gate.qualified ? ' isPass' : ' isNone') : ' isUnscored'}`}
            title={d.gate
              ? `${d.gate.qualified} of ${d.gate.scored} scored cabs met the gate (${d.gate.rule}) and earned ${inr(d.gate.rupees)} of the day's ${inr(d.rupees)}`
              : 'The eligibility sweep has not reached this day'}
          >
            <b>GATE</b>
            {d.gate ? (
              <>
                <span className="gateNum">{d.gate.qualified}<i>/{d.gate.scored}</i></span>
                <span className="gateCash">{d.gate.rupees > 0 ? inr(d.gate.rupees) : '₹0'}</span>
              </>
            ) : <span className="gateNum"><i>not scored</i></span>}
          </span>
        </>
      )}

      {milestone && <span className="dayFlag">{milestone.kind === 'removed' ? '−' : '+'}{milestone.screens}</span>}
    </button>
  );
}

// THE TAPE — one row per cab, one column per day.
//
// Grafted from a design pass in which three independent directions arrived at
// the same encoding unprompted: POSITION IS IDENTITY. A cab holds its row for
// the life of the record, so a screen that stops delivering leaves a hole in
// the same place every day and four weeks of silence reads as a stripe. The
// aggregate cards below cannot show that — three dark cabs and one dark cab
// both read there as simply "fewer today".
function Tape({ tape, days, selected, onSelect }) {
  const CH = { X: 'ran', o: 'silent', L: 'lag', '.': 'unfitted' };
  // A month label every four weeks is not a timeline — it is a caption. So the
  // ruler carries a dated tick on every Monday and a heavier one at each month
  // boundary, and the weekend columns are banded behind the cells, which is
  // what lets a reader land on a column and know which day it is.
  const ticks = [];
  days.forEach((d, i) => {
    const isMonthStart = i === 0 || d.day.slice(5, 7) !== days[i - 1].day.slice(5, 7);
    const isMonday = d.isoDow === 1;
    if (!isMonthStart && !isMonday) return;
    const dt = new Date(`${d.day}T00:00:00Z`);
    ticks.push({
      i,
      month: isMonthStart,
      label: isMonthStart
        ? dt.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' }).toUpperCase()
        : String(Number(d.day.slice(8, 10))),
    });
  });

  return (
    <section className="tape">
      <header className="tapeHead">
        <h2 className="tapeTitle"><Term k="tape">Every cab, every day</Term></h2>
        <p className="tapeNote">{tape.note}</p>
      </header>

      {/* Two rows, not one. Month labels and Monday day-numbers sharing a single
          grid row overlapped wherever a month began near a Monday — which is
          most of them — and the ruler read as noise rather than a timeline. */}
      <div className="tapeRow tapeRuler">
        <span className="tapeMoneyHead">
          {(tape.monthKeys || []).map((k) => (
            <span key={k}>{new Date(`${k}-01T00:00:00Z`)
              .toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' })}</span>
          ))}
        </span>
        <span />
        <span className="tapeTrack tapeMonths" style={{ '--cols': days.length }}>
          {ticks.filter((t) => t.month).map((t, k, arr) => (
            <span
              key={t.i}
              className="tapeMonth"
              style={{ gridColumn: `${t.i + 1} / ${(arr[k + 1]?.i ?? days.length) + 1}` }}
            >
              {t.label}
            </span>
          ))}
        </span>
        <span />
      </div>
      <div className="tapeRow tapeRuler">
        <span />
        <span />
        <span className="tapeTrack tapeMarks" style={{ '--cols': days.length }}>
          {ticks.filter((t) => !t.month).map((t) => (
            <span key={t.i} className="tapeMark" style={{ gridColumn: `${t.i + 1} / span 7` }}>
              {t.label}
            </span>
          ))}
        </span>
        <span />
      </div>
      <div className="tapeRow tapeTicksRow">
        <span />
        <span />
        <span className="tapeTrack" style={{ '--cols': days.length }}>
          {days.map((d, i) => (
            <span
              key={d.day}
              className={`tick${d.isoDow === 1 ? ' isWeek' : ''}${d.isoDow >= 6 ? ' isWeekend' : ''}`}
            />
          ))}
        </span>
        <span />
      </div>

      {tape.cabs.map((c, idx) => {
        const prev = tape.cabs[idx - 1];
        const cityBreak = !prev || prev.city !== c.city;
        return (
          <React.Fragment key={`${c.plate}|${c.nodeName}|${c.fitted}`}>
            {cityBreak && <p className="tapeCity">{c.city === 'mumbai' ? 'Mumbai' : 'Pune'}</p>}
            <div className={`tapeRow${c.isActive ? '' : ' isLapsed'}`}>
              {/* Presence money only, month by month, to the left of the cab —
                  the same rupees the day cards carry, cut by screen. */}
              <span className="tapeMoney">
                {(tape.monthKeys || []).map((k) => (
                  <span key={k} className={c.months?.[k] ? 'mVal' : 'mNil'}>
                    {c.months?.[k] ? Math.round(c.months[k]).toLocaleString('en-IN') : '·'}
                  </span>
                ))}
              </span>
              <span className="tapePlate">
                {c.plate}
                {!c.isActive && <em className="tapeOff">off {c.removed?.slice(5)}</em>}
              </span>
              <span className="tapeTrack" style={{ '--cols': days.length }}>
                {c.track.split('').map((ch, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`cell is-${CH[ch] || 'unfitted'}`
                      + `${days[i]?.isoDow >= 6 ? ' is-weekendCol' : ''}`
                      + `${selected?.day === days[i]?.day ? ' is-on' : ''}`}
                    title={`${c.plate} · ${days[i]?.dow} ${days[i]?.day} · ${CH[ch]}`}
                    onClick={() => days[i] && onSelect(days[i])}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                ))}
              </span>
              <span className={`tapeDark${c.daysDark >= 14 ? ' isBad' : ''}`}>
                {c.daysDark === 0 ? 'today' : `${c.daysDark}d`}
              </span>
            </div>
          </React.Fragment>
        );
      })}

      <ul className="tapeKey">
        <li><span className="cell is-ran" /> <Term k="tapeDelivered">delivered</Term></li>
        <li><span className="cell is-silent" /> <Term k="tapeSilent">fitted and silent</Term></li>
        <li><span className="cell is-lag" /> <Term k="tapeLag">delivered before the registry had it fitted</Term></li>
        <li><span className="cell is-unfitted" /> <Term k="tapeUnfitted">not yet fitted</Term></li>
        <li className="tapeKeyNote"><Term k="tapeMonths">left figures are presence-verified rupees by month</Term> · <Term k="tapeDark">right figure is days since that cab last delivered</Term></li>
      </ul>
    </section>
  );
}

function Month({ month, milestones, todayKey, selected, onSelect, measure }) {
  // The same chunking the route used to build month.weeks, so cell and
  // summary always line up.
  const cells = [...Array(month.leadingBlanks).fill(null), ...month.days];
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  const sm = month.summary || {};
  const ec = month.economics;
  const pt = month.summary?.potential;
  return (
    <section className="month">
      <div className="weekHead" aria-hidden="true">
        <span className="weekHeadSum"><Term k="monthRevenue">month</Term></span>
        <span className="weekHeadSum"><Term k="potential">potential</Term></span>
        <span className="weekHeadSum"><Term k="economics">economics</Term></span>
        <span className="weekHeadSum"><Term k="weekCell">week</Term></span>
        {WEEK.map((w) => <span key={w} className={w === 'Sat' || w === 'Sun' ? 'weekEnd' : undefined}>{w}</span>)}
      </div>

      {/* Eight columns, not seven: the week summary is a CELL IN THE SAME GRID
          ROW as the days it summarises, so the two can never drift apart the
          way a floating side panel would. */}
      <div className="grid">
        {/* The month consolidation spans every week row of its own grid, so it
            is physically the same object as the weeks and days it totals. */}
        <div className="monthCard" style={{ gridRow: `1 / span ${rows.length}`, gridColumn: 1 }}>
          <h2 className="monthCardName">{month.label}</h2>
          <span className="monthCardMoney">{inr(sm.rupees)}</span>
          {sm.momPct != null && (
            <span className={`monthCardMom${sm.momPct < 0 ? ' isDown' : ''}`}>
              {sm.momPct > 0 ? '+' : ''}{sm.momPct}% <Term k="momPct">on last month</Term>
              {/* A month-to-date total against a full month is not a
                  comparison — for a part month the per-day delta is the one
                  that means anything. */}
              {sm.partial && sm.momPerDayPct != null && (
                <em className="momDaily">
                  {sm.momPerDayPct > 0 ? '+' : ''}{sm.momPerDayPct}% per day, like for like
                </em>
              )}
            </span>
          )}
          <dl className="monthCardRows">
            <div>
              <dt><Term k="screensFitted">Screens fitted</Term></dt>
              <dd>{sm.deployedStart === sm.deployedEnd
                ? sm.deployedEnd
                : <>{sm.deployedStart} → {sm.deployedEnd}</>}</dd>
            </div>
            <div><dt><Term k="typicallyLive">Typically live</Term></dt><dd>{sm.avgActive ?? '—'}<span className="mcUnit">of {sm.deployedEnd}</span></dd></div>
            <div><dt><Term k="ranPct">Ran</Term></dt><dd>{sm.ranPct == null ? '—' : `${sm.ranPct}%`}</dd></div>
            <div><dt><Term k="perScreenDay">Per screen-day</Term></dt><dd>{inr(sm.perScreenDay)}</dd></div>
            <div><dt><Term k="plays">Plays</Term></dt><dd>{n(sm.plays)}</dd></div>
            <div><dt><Term k="distance">Distance</Term></dt><dd>{n(sm.km)}<span className="mcUnit">km</span></dd></div>
            <div><dt><Term k="trips">Trips</Term></dt><dd>{n(sm.trips)}</dd></div>
            <div><dt><Term k="paxHours">Passenger hrs</Term></dt><dd>{n(sm.paxHours)}</dd></div>
            <div><dt><Term k="driveHours">Driving hrs</Term></dt><dd>{n(sm.driveHours)}</dd></div>
            <div className="mcGate">
              <dt><Term k="metGateMonth">Met the gate</Term></dt>
              <dd>{n(sm.gateQualified)}<span className="mcUnit">of {n(sm.gateScored)} cab-days</span></dd>
            </div>
            <div>
              <dt><Term k="earnedByThose">Earned by those</Term></dt>
              <dd>{inr(sm.gateRupees)}<span className="mcUnit">
                {sm.rupees ? `${Math.round((sm.gateRupees / sm.rupees) * 100)}%` : ''}
              </span></dd>
            </div>
            {sm.peak && (
              <div><dt><Term k="monthPeak">Best day</Term></dt><dd>{sm.peak.active}<span className="mcUnit">{sm.peak.dow} {Number(sm.peak.day.slice(8, 10))}</span></dd></div>
            )}
          </dl>
          {sm.gateRule && <p className="monthCardNote"><Term k="gateRule">Gate this month</Term>: {sm.gateRule}.</p>}
          {sm.partial && <p className="monthCardNote"><Term k="partMonth">Part month</Term> — {sm.daysServed} days so far.</p>}
        </div>
        {pt && (
          <div className="pot" style={{ gridRow: `1 / span ${rows.length}`, gridColumn: 2 }}>
            {/* What the same fitted cabs could have earned at per-cab-day rates
                this fleet has ALREADY achieved. A ceiling, not a forecast —
                and the gap is fleet-time that earned nothing, not ambition. */}
            <span className="econTitle"><Term k="potential">What it could be</Term></span>

            <div className="potActual">
              <span className="potActualLabel"><Term k="potActual">Actual</Term></span>
              <span className="potActualVal">{inr(sm.rupees, 0)}</span>
              <span className="potActualSub">{inr(pt.perCabDayActual, 0)} per cab-day that ran</span>
            </div>

            {/* TWO BASES, because there are two levers. The left column holds
                utilisation where it is and moves yield alone; the right adds
                the fleet-time that earned nothing. Which lever is bigger
                differs by month, and one basis alone cannot say. */}
            <div className="potTable">
              <div className="potHead">
                <span />
                <span><Term k="potRan">× ran</Term><i>{n(pt.ranCabDays)}</i></span>
                <span><Term k="potFitted">× fitted</Term><i>{n(pt.fittedCabDays)}</i></span>
              </div>
              {pt.rungs.map((r) => (
                <div className="potRow" key={r.key}>
                  <span className="potRowLabel">
                    <Term k={{ p50: 'potMedian', p75: 'potP75', best: 'potBest', ever: 'potBest' }[r.key]}>{r.label}</Term>
                    <em>{inr(r.rate, 0)}</em>
                  </span>
                  <span className="potCell">
                    {inr(r.onRan, 0)}<i>{(r.onRan / sm.rupees).toFixed(1)}×</i>
                  </span>
                  <span className="potCell isFitted">
                    {inr(r.onFitted, 0)}<i>{(r.onFitted / sm.rupees).toFixed(1)}×</i>
                  </span>
                </div>
              ))}
            </div>

            <dl className="potLevers">
              <div>
                <dt><Term k="levers">Yield alone</Term></dt>
                <dd>{(pt.levers.yieldOnly / sm.rupees).toFixed(1)}×</dd>
              </div>
              <div className={pt.levers.utilisationOnly > pt.levers.yieldOnly ? 'isBigger' : undefined}>
                <dt><Term k="levers">Utilisation alone</Term></dt>
                <dd>{(pt.levers.utilisationOnly / sm.rupees).toFixed(1)}×</dd>
              </div>
              <div>
                <dt>Both</dt>
                <dd>{(pt.levers.both / sm.rupees).toFixed(1)}×</dd>
              </div>
            </dl>

            <p className="econNote">{pt.note}</p>
          </div>
        )}
        {ec && (
          <div className="econ" style={{ gridRow: `1 / span ${rows.length}`, gridColumn: 3 }}>
            {/* Where every rupee of this month's presence revenue ends up.
                Rates are read from the dated agreement rows, so a term that
                changes forward at a month boundary changes this by itself. */}
            <span className="econTitle"><Term k="economics">Where it goes</Term></span>

            <div className="econSplit" title="Supply, partners, pDOOH.ai">
              <span className="segSupply" style={{ '--w': ec.toSupply / ec.gross }} />
              <span className="segPartner" style={{ '--w': ec.partners.amount / ec.gross }} />
              <span className="segUs" style={{ '--w': ec.pdoohNet / ec.gross }} />
            </div>
            <div className="econSplitKey">
              <Term k="splitBar" className="econSplitTerm">
                <span><i className="kSupply" />supply, net {Math.round((ec.toSupply / ec.gross) * 100)}%</span>
              </Term>
              <span><i className="kPartner" />partners {ec.partners.pct}%</span>
              <span><i className="kUs" />pDOOH.ai {Math.round((ec.pdoohNet / ec.gross) * 100)}%</span>
            </div>

            <dl className="econRows">
              <div className="eTop"><dt><Term k="monthRevenue">Presence revenue</Term></dt><dd>{inr(ec.gross)}</dd></div>
              <div className="eMinus">
                <dt>− <Term k="technology">{ec.tech.label}</Term> <em>{ec.tech.pct}% of gross</em></dt>
                <dd>−{inr(ec.tech.amount)}</dd>
              </div>
              <div className="eSum"><dt><Term k="pool">Pool</Term></dt><dd>{inr(ec.pool)}</dd></div>

              <div className="eHead">
                <dt><Term k="metGateEcon">Met the gate</Term></dt>
                <dd>{inr(ec.qualified.pool)}</dd>
              </div>
              {ec.qualified.unowned && (
                <div className="eCarve">
                  <dt><Term k="noOwner">{ec.qualified.unowned.label}</Term></dt>
                  <dd>−{inr(ec.qualified.unowned.amount)}</dd>
                </div>
              )}
              {/* The rates are taken of THIS, not of the pool. Printing it
                  makes the three rows below sum to something on the screen. */}
              <div className="eSum eShareable">
                <dt><Term k="shareablePool">Shareable</Term></dt>
                <dd>{inr(ec.qualified.shareablePool)}</dd>
              </div>
              {[
                { r: ec.qualified.premise, k: 'premiseOwner' },
                { r: ec.qualified.device, k: 'deviceOwner' },
                { r: ec.qualified.margin, k: 'platformMargin' },
              ].map(({ r, k }) => (
                <div className="eChild" key={k}>
                  <dt><Term k={k}>{r.label}</Term> <em>{r.pct}%</em></dt>
                  <dd>{inr(r.amount)}</dd>
                </div>
              ))}

              <div className="eHead eKeep">
                <dt><Term k="didNotMeet">Did not meet the gate</Term></dt>
                <dd>{inr(ec.unqualified.pool)}</dd>
              </div>
              <div className="eChild"><dt>pDOOH.ai keeps all of it</dt><dd /></div>

              {ec.unscored && (
                <>
                  <div className="eHead eUnscored">
                    <dt><Term k="notScored">{ec.unscored.label}</Term></dt>
                    <dd>{inr(ec.unscored.pool)}</dd>
                  </div>
                  <div className="eChild"><dt><Term k="notScored">held with pDOOH.ai until scored</Term></dt><dd /></div>
                </>
              )}

              <div className="eTotal"><dt><Term k="supplyShare">Supply share</Term></dt><dd>{inr(ec.supplyGross)}</dd></div>
              {ec.sim && (
                <div className="eCarve">
                  <dt>− <Term k="simRecovery">SIM recovery</Term> <em>{ec.sim.devices} × {inr(ec.sim.perDeviceMonth)}, ungated</em></dt>
                  <dd>−{inr(ec.sim.amount)}</dd>
                </div>
              )}
              <div className={`eTotal${ec.toSupply < 0 ? ' isNeg' : ''}`}>
                <dt><Term k="toSupplyNet">To supply, net</Term></dt><dd>{inr(ec.toSupply)}</dd>
              </div>
              <div className="eTotal"><dt><Term k="pdoohTake">pDOOH.ai take</Term></dt><dd>{inr(ec.pdoohTotal)}</dd></div>
              <div className="eCarve">
                <dt>− <Term k="partners">{ec.partners.label}</Term> <em>{ec.partners.pct}% {ec.partners.basis}, notional</em></dt>
                <dd>−{inr(ec.partners.amount)}</dd>
              </div>
              <div className="eTotal eNet"><dt><Term k="pdoohNet">pDOOH.ai net</Term></dt><dd>{inr(ec.pdoohNet)}</dd></div>
            </dl>

            {ec.toSupply < 0 && (
              <p className="econNote isWarn">
                SIM recovery exceeds the share this month — the operator ends it owing us, before
                the ₹1,000 carry the real close applies.
              </p>
            )}
            <p className="econNote">{ec.partners.note} It is an internal planning figure, not an agreement term.</p>
            <p className="econNote">{ec.note}</p>
          </div>
        )}
        {rows.map((row, wi) => {
          const w = month.weeks?.[wi];
          return (
            <React.Fragment key={w?.from || wi}>
              <div className="week">
                <span className="weekRange">{w?.label}</span>
                <span className="weekMoney">{w ? measure.fmt(w[measure.id]) : '—'}</span>
                {measure.id !== 'rupees' && (
                  <span className="weekMeta weekAlt">{w?.rupees > 0 ? inr(w.rupees) : ''}</span>
                )}
                <span className="weekMeta">
                  {w?.avgActive != null
                    ? <>{w.avgActive} of {w.deployedEnd} live · {w.ranPct}%</>
                    : <>{w?.deployedEnd} fitted</>}
                </span>
                <span className="weekMeta weekPlays">{w?.plays ? `${n(w.plays)} plays` : ''}</span>
                {w?.gateScored > 0 && (
                  <span className="weekGate">
                    gate {w.gateQualified}<i>/{w.gateScored}</i>
                    <i> · {inr(w.gateRupees)}</i>
                  </span>
                )}
              </div>
              {row.map((d, ci) => (d ? (
                <DayCard
                  key={d.day}
                  d={d}
                  milestone={milestones[d.day]}
                  isToday={d.day === todayKey}
                  selected={selected?.day === d.day}
                  onSelect={onSelect}
                  measure={measure}
                />
              ) : <span key={`b${wi}-${ci}`} className="blank" />))}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

export default function Calendar() {
  const { data, error, loading, reload } = useApi(
    () => api.get('/atlas/calendar').then((r) => r.data), []);
  const [selected, setSelected] = useState(null);
  const [measureId, setMeasureId] = useState('rupees');
  const measure = measureOf(measureId);

  // One milestone per day wins the flag; a day that both gained and lost a
  // screen (20 Aug, one cab moving tenant) shows the gain.
  const milestones = useMemo(() => {
    const by = {};
    for (const m of data?.milestones || []) {
      if (!by[m.day] || m.kind === 'fitted') by[m.day] = m;
    }
    return by;
  }, [data]);

  if (loading) return <div className="atlasState">Reading production…</div>;
  if (error) {
    return (
      <div className="atlasState atlasStateErr">
        <p>Could not build the calendar.</p>
        <p className="atlasStateDetail">{String(error)}</p>
        <button type="button" className="atlasBtn" onClick={reload}>Try again</button>
      </div>
    );
  }
  if (!data) return null;

  const t = data.today;
  const idle = t.deployed.total - t.active.total;
  const day = selected || data.days[data.days.length - 1];

  return (
    <div className="calPage">
      <header className="calHead">
        <div className="calTitles">
          <p className="calKicker">{data.fleet.operator} · Mumbai and Pune</p>
          <h1 className="calTitle">Deployment</h1>
          <p className="calLede">
            Every day since the first cab was fitted. The top number is what actually ran;
            the number after the slash is what was fitted and should have.
          </p>
        </div>

        <div className="calRight">
          <Term k="measureSwitch" className="measureSwitchTerm"><span className="measureSwitchLabel">measure</span></Term>
            <div className="measureSwitch" role="group" aria-label="Measure shown on each day">
            {MEASURES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`measureBtn${m.id === measureId ? ' isOn' : ''}`}
                onClick={() => setMeasureId(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        <dl className="calFacts">
          <div>
            <dt><Term k="fittedToday">Fitted today</Term></dt>
            <dd>{t.deployed.total}</dd>
            <dd className="calFactSub">{t.deployed.mumbai} Mumbai · {t.deployed.pune} Pune</dd>
          </div>
          <div>
            <dt><Term k="ranToday">Ran today</Term></dt>
            <dd>{t.active.total}</dd>
            <dd className="calFactSub">{t.active.mumbai} Mumbai · {t.active.pune} Pune</dd>
          </div>
          <div className={idle > 0 ? 'isWarn' : undefined}>
            <dt><Term k="fittedSilent">Fitted and silent</Term></dt>
            <dd>{idle}</dd>
            <dd className="calFactSub">Deployed screens that did not run</dd>
          </div>
          <div>
            <dt><Term k="bestDayEver">Best day ever</Term></dt>
            <dd>{data.peak.active.total}</dd>
            <dd className="calFactSub">{data.peak.day}</dd>
          </div>
        </dl>
        </div>
      </header>

      {data.tape && (
        <Tape
          tape={data.tape}
          days={data.days}
          selected={selected}
          onSelect={(d) => setSelected((cur) => (cur?.day === d.day ? null : d))}
        />
      )}

      <div className="months">
        {data.months.map((m) => (
          <Month
            key={m.key}
            month={m}
            milestones={milestones}
            todayKey={t.day}
            selected={selected}
            onSelect={(d) => setSelected((cur) => (cur?.day === d.day ? null : d))}
            measure={measure}
          />
        ))}
      </div>

      <aside className="dayPanel" aria-live="polite">
        <p className="panelKicker">{selected ? 'Selected day' : 'Today'}</p>
        <h3 className="panelDay">{day.dow} {day.day}</h3>
        <dl className="panelRows">
          <div><dt><Term k="panelFitted">Screens fitted</Term></dt><dd>{day.deployed.total}</dd></div>
          <div><dt><Term k="panelFitted">Screens that ran</Term></dt><dd>{day.active.total}</dd></div>
          <div><dt>Mumbai</dt><dd>{day.active.mumbai} of {day.deployed.mumbai}</dd></div>
          <div><dt>Pune</dt><dd>{day.active.pune} of {day.deployed.pune}</dd></div>
          <div><dt><Term k="plays">Plays</Term></dt><dd>{n(day.plays)}</dd></div>
          <div><dt><Term k="monthRevenue">Accrued on presence</Term></dt><dd>{inr(day.rupees)}</dd></div>
          <div><dt><Term k="distance">Distance</Term></dt><dd>{n(day.km)}<span className="panelUnit">km</span></dd></div>
          <div><dt><Term k="trips">Trips</Term></dt><dd>{n(day.trips)}</dd></div>
          <div><dt><Term k="paxHours">Passenger hrs</Term></dt><dd>{n(day.paxHours)}</dd></div>
          <div><dt><Term k="driveHours">Driving hrs</Term></dt><dd>{n(day.driveHours)}</dd></div>
          <div>
            <dt><Term k="dayGate">Met the gate</Term></dt>
            <dd>{day.gate ? <>{day.gate.qualified}<span className="panelUnit">of {day.gate.scored} scored</span></> : '—'}</dd>
          </div>
          <div>
            <dt><Term k="earnedByThose">Earned by those</Term></dt>
            <dd>{day.gate ? inr(day.gate.rupees) : '—'}</dd>
          </div>
        </dl>
        {day.spread && (
          <div className="panelSpread">
            <p className="panelSpreadHead"><Term k="daySpread">spread</Term>{' '}
              {day.spread.earners} cabs earned · best {inr(day.spread.bestCab)} · median {inr(day.spread.medianCab)}
            </p>
            <p className="panelNote">
              The best cab took <b>{day.spread.topShare}%</b> of the day. Cabs do not contribute
              equally — a fleet average would hide that.
            </p>
          </div>
        )}
        {milestones[day.day] && (
          <p className="panelNote">
            {milestones[day.day].kind === 'fitted' ? 'Fitted' : 'Removed'} this day:{' '}
            {milestones[day.day].plates} ({milestones[day.day].city})
          </p>
        )}
        {day.gate
          ? <p className="panelNote"><Term k="gateRule">Gate in force that day</Term>: <b>{day.gate.rule}</b>. Frozen verdicts from the payout plane.</p>
          : <p className="panelNote isLag">The eligibility sweep has not scored this day yet.</p>}
        {day.lag && (
          <p className="panelNote isLag">
            {day.lag.screens} screen{day.lag.screens > 1 ? 's' : ''} delivered before their binding was
            written, earning {inr(day.lag.rupees)} that sits outside this page's totals.
            {' '}{data.legend.registryLag}
          </p>
        )}
        <p className="panelHint">Pick any date to hold it here.</p>
      </aside>

      <footer className="calLegend">
        <ul>
          <li><span className="key key-full" /> <Term k="dayStates">every fitted screen ran</Term></li>
          <li><span className="key key-partial" /> <Term k="dayStates">some of the fitted screens ran</Term></li>
          <li><span className="key key-dark" /> <Term k="dayStates">fitted, none ran</Term></li>
          <li><span className="key key-pre" /> <Term k="preService">fitted before the network began serving</Term></li>
          <li><span className="key key-lag" /> <Term k="dayAhead">more ran than the registry had fitted</Term></li>
          <li><span className="key key-milestone" /> <Term k="dayMilestone">screens fitted or removed that day</Term></li>
          <li className="calFootInline"><Term k="dayGate">GATE = cabs that met the day gate, of those scored, and what they earned</Term> · <Term k="dayCount">n / N</Term> · <Term k="dayPct">the percentage</Term> · <Term k="dayCities">M · P</Term></li>
        </ul>
        <p className="calFoot">{data.earlier.note}</p>
        <p className="calFoot">Read from production · {data.ist}</p>
      </footer>
    </div>
  );
}
